import { useEffect, useState } from 'react';
import { Title } from 'react-admin';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { Tile, TileGrid } from '../dashboard/Tile';
import { buildListUrl } from '../dashboard/listUrl';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { fmtDateTimeShort } from '../../shared/listCells';
import {
  INTAKE_TILE_DESCRIPTIONS,
  INTAKE_TILE_HIGHLIGHT,
  fetchIntakeDashboard,
  type IntakeDashboardSummary,
  type IntakeTile,
} from './intakeApi';
import { FEATURE_INTAKE_DEPARTMENTS, INTAKE_SIDE_LABELS, type IntakeSide } from './intakeSides';

/**
 * Pulpit przyjęcia zleceń (makieta back office v2, fala B, B1: „Pulpit CS”,
 * „Pulpit Administracji”; przełącznik `intakeDepartments`, S-21.10-01).
 * Sekcja każdego działu pytającego — CS: do uzupełnienia, zwrócone
 * z pytaniem, uwagi od FTL, zmiany do decyzji; Administracja: do kontroli,
 * zatwierdzone przed plikiem, błąd zapisu, plik czeka na TMS, zmiany do
 * wprowadzenia w TMS. Każda liczba prowadzi do listy z filtrem, którym API ją
 * policzyło (kafelek bierze filtr z odpowiedzi).
 *
 * Makieta ma też „Do rozliczenia” i „Ostatnie zapisy z numerami TMS” — poza
 * wersją lekką: rozliczenia nie ma w projekcie, a numer TMS wraca importem
 * („Jest w TMS” na karcie i w etapie prezlecenia).
 */

export const FEATURE_OFF_NOTE =
  'Pulpit przyjęcia zleceń jest wyłączony w wersji aplikacji, z której korzystasz.';
export const NO_PERMISSION_NOTE = 'Pulpit przyjęcia zleceń jest dla CS i Administracji.';
export const LOAD_ERROR = 'Nie udało się wczytać pulpitu przyjęcia zleceń. Odśwież stronę.';

export const IntakeDashboard = () => {
  const on = useFeatureState(FEATURE_INTAKE_DEPARTMENTS);
  const canIntake = useHasPermission('ORDER_INTAKE');
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !canIntake) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('intake-dashboard')} />
        <Alert severity="info">{on ? NO_PERMISSION_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <IntakeDashboardScreen />;
};

const tileTarget = (t: IntakeTile): string => {
  if (t.target === 'ftl-notes') return '/ftl-notes';
  return buildListUrl(t.target, t.filter);
};

const SideSection = ({ side, tiles }: { side: IntakeSide; tiles: IntakeTile[] }) => (
  <Box data-testid={`intake-dashboard-${side}`}>
    <Typography variant="h6" gutterBottom>
      {INTAKE_SIDE_LABELS[side]}
    </Typography>
    <TileGrid>
      {tiles.map((t) => (
        <Box key={t.key} data-testid={`intake-tile-${t.key}`} data-count={t.count}>
          <Tile
            label={t.label}
            value={t.count}
            description={INTAKE_TILE_DESCRIPTIONS[t.key] ?? ''}
            linkTo={tileTarget(t)}
            highlight={t.count > 0 ? INTAKE_TILE_HIGHLIGHT[t.key] : undefined}
          />
        </Box>
      ))}
    </TileGrid>
  </Box>
);

const IntakeDashboardScreen = () => {
  const [summary, setSummary] = useState<IntakeDashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchIntakeDashboard()
      .then((s) => {
        if (active) setSummary(s);
      })
      .catch(() => {
        if (active) setError(LOAD_ERROR);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="intake-dashboard">
      <Title title={menuLabel('intake-dashboard')} />
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!summary && !error ? (
        <TileGrid>
          <Tile label="Wczytuję…" loading />
        </TileGrid>
      ) : null}
      {summary?.sides.map((side) => (
        <SideSection key={side} side={side} tiles={summary.tiles.filter((t) => t.side === side)} />
      ))}
      {summary?.computedAt ? (
        <Typography variant="caption" color="text.secondary">
          Stan na {fmtDateTimeShort(summary.computedAt)} — liczby to te same filtry co listy prezleceń, uwag
          i zmian zleceń.
        </Typography>
      ) : null}
    </Stack>
  );
};
